import { useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfigSelect } from "@/components/config-select";
import {
  configOptions,
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  TIMEZONE_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
} from "@/lib/config-options";
import { Skeleton } from "@/components/ui/skeleton";
import { BrandLogoField } from "@/components/brand-logo-field";
import { BrandColourField } from "@/components/brand-colour-field";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { appLocales, localeNames } from "@/i18n/config";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** The canonical list, not a second `["en","de"]` (POS2-1146). `appLocales` is the subset that
 * ships a catalogue, so a locale reaches this picker only once it renders as itself rather than
 * as English — and `localeNames` gives endonyms, since a German admin scans for "Deutsch". */
const LANGUAGE_OPTIONS = appLocales.map((locale) => ({
  value: locale,
  label: localeNames[locale],
}));

interface Org {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  default_weight_unit: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  logo_url: string | null;
  logo_key: string | null;
  default_locale: string | null;
  primary_color: string | null;
  food_cost_target_pct: number | null;
  country_code: string | null;
  waste_pct_target: number | null;
  waste_per_cover_target_g: number | null;
}

/** Built inside the component and memoised on `t`, so the messages are the reader's — a
 * module-level schema would freeze whichever locale happened to be active at import
 * (POS2-1133 pinned decision, the `makeSchema(t)` factory CP3a landed). */
function makeOrgUpdateSchema(t: TFunction) {
  return z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    timezone: z
      .string()
      .min(1)
      .refine((tz) => (Intl.supportedValuesOf?.("timeZone") ?? []).includes(tz), {
        message: "Pick a timezone from the list",
      }),
    currency: z.string().min(1),
    default_weight_unit: z.string().min(1),
    contact_email: z.string().email(t("validation.invalidEmail")).or(z.literal("")).optional(),
    contact_phone: z.string().optional(),
    address: z.string().optional(),
    primary_color: z.string().optional(),
    // Deliberately unconstrained, like `currency` above and unlike `timezone`. The `<select>`
    // can only hold "", an `appLocales` value, or whatever is already stored — and a stored
    // value outside the list is exactly what `withCurrentValue` keeps selectable. A membership
    // check here would make such a row fail validation and disable Save with nothing on screen
    // to say why, which is the POS2-1140 defect the timezone `trigger` below exists to explain.
    // The list is the menu; `OrgUpdate.default_locale` is the contract.
    default_locale: z.string().optional(),
    country_code: z.string().optional(),
    food_cost_target_pct: z.coerce
      .number()
      .min(0, "Must be 0 or above")
      .max(100, "Must be 100 or below")
      .optional()
      .or(z.literal("")),
    waste_pct_target: z.coerce
      .number()
      .min(0, "Must be 0 or above")
      .max(100, "Must be 100 or below")
      .optional()
      .or(z.literal("")),
    waste_per_cover_target_g: z.coerce
      .number()
      .int("Must be whole grams")
      .positive("Must be above 0")
      .optional()
      .or(z.literal("")),
  });
}

type OrgUpdateForm = z.infer<ReturnType<typeof makeOrgUpdateSchema>>;

export default function SettingsOrganisationPage() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  const orgQuery = useQuery<Org>({
    queryKey: ["organisation", "me"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/organisations/me`, { headers });
      if (!res.ok) throw new Error("Failed to fetch organisation");
      const json = await res.json();
      return json.data;
    },
  });

  // Read-only: which catalogue this org draws from. Switching it is
  // platform-admin-only (POS2-789), so org admins see it but can't change it.
  const activeCatalogueQuery = useQuery<string | null>({
    queryKey: ["organisation", "active-catalogue"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory/available-catalogues`, { headers });
      if (!res.ok) throw new Error("Failed to fetch active catalogue");
      const json = await res.json();
      const catalogues: { name: string; is_active: boolean }[] = json.data.catalogues ?? [];
      return catalogues.find((c) => c.is_active)?.name ?? null;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: OrgUpdateForm) => {
      const body = {
        ...data,
        contact_email: data.contact_email || null,
        contact_phone: data.contact_phone || null,
        address: data.address || null,
        primary_color: data.primary_color || null,
        country_code: data.country_code || null,
        // Null rather than "": the API's `Locale` refuses an empty string, and `exclude_none`
        // drops a null — so an unset language stays unset instead of failing the whole save.
        // Clearing a language that *is* set is not possible through this PATCH at all, which is
        // why "Not set" is only offered while it already is.
        default_locale: data.default_locale || null,
        food_cost_target_pct:
          data.food_cost_target_pct !== "" && data.food_cost_target_pct !== undefined
            ? Number(data.food_cost_target_pct)
            : null,
        waste_pct_target:
          data.waste_pct_target !== "" && data.waste_pct_target !== undefined
            ? Number(data.waste_pct_target)
            : null,
        waste_per_cover_target_g:
          data.waste_per_cover_target_g !== "" && data.waste_per_cover_target_g !== undefined
            ? Number(data.waste_per_cover_target_g)
            : null,
      };
      const res = await fetch(`${API_URL}/api/v1/organisations/me`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation", "me"] });
      // The org rung of the locale chain and the brand colour both come from
      // `GET /organisations/me/brand`, which this page does not read — so without this a saved
      // language would be stored and not shown, and the admin would have to reload to see the
      // choice they just made take effect (POS2-1146).
      queryClient.invalidateQueries({ queryKey: ["organisation", "brand"] });
      toast.success(t("settings.organisationUpdated"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<OrgUpdateForm>({
    resolver: zodResolver(useMemo(() => makeOrgUpdateSchema(t), [t])),
  });
  // Watched rather than registered, because `BrandColourField` drives two inputs off one value —
  // an uncontrolled pair only ever updates the one that was touched.
  const colour = watch("primary_color");

  useEffect(() => {
    if (orgQuery.data) {
      reset({
        name: orgQuery.data.name,
        timezone: orgQuery.data.timezone,
        currency: orgQuery.data.currency,
        default_weight_unit: orgQuery.data.default_weight_unit,
        contact_email: orgQuery.data.contact_email ?? "",
        contact_phone: orgQuery.data.contact_phone ?? "",
        address: orgQuery.data.address ?? "",
        primary_color: orgQuery.data.primary_color ?? "",
        default_locale: orgQuery.data.default_locale ?? "",
        country_code: orgQuery.data.country_code ?? "",
        food_cost_target_pct: orgQuery.data.food_cost_target_pct ?? "",
        waste_pct_target: orgQuery.data.waste_pct_target ?? "",
        waste_per_cover_target_g: orgQuery.data.waste_per_cover_target_g ?? "",
      });
    }
  }, [orgQuery.data, reset]);

  // A row stored before the timezone constraint can hold a value the schema rejects,
  // which makes the form invalid and so disables Save. Validate that one field on load so
  // the reason is on screen rather than left to be guessed (POS2-1140).
  //
  // Deliberately a separate effect: `trigger` in the reset effect's dependencies made it
  // re-run and wipe values the user had typed, which disabled Save with no explanation —
  // caught by an unrelated test. On its own, a re-run only re-validates one field.
  useEffect(() => {
    if (orgQuery.data) void trigger("timezone");
  }, [orgQuery.data, trigger]);

  if (orgQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (orgQuery.isError) {
    return <p className="text-sm text-destructive">{t("settings.organisationLoadFailed")}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium">{t("settings.organisationTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings.organisationDescription")}
        </p>
      </div>

      <form onSubmit={handleSubmit((data) => updateMutation.mutate(data))}>
        {/* Profile */}
        <h2 className="text-sm font-semibold">{t("settings.profile")}</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium" htmlFor="name">
              {t("settings.name")}
            </label>
            <Input id="name" {...register("name")} />
            {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="slug">
              {t("settings.slug")}
            </label>
            <Input id="slug" value={orgQuery.data?.slug ?? ""} disabled />
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.cannotBeChanged")}</p>
          </div>
        </div>

        {/* Defaults */}
        <div className="mt-8 border-t border-border/50 pt-8">
          <h2 className="text-sm font-semibold">{t("settings.defaults")}</h2>
          {/* All five on one three-column grid, so Country and Language sit in the columns
              Timezone and Currency already define. Two grids of different widths made every
              field in the second row wider than the one above it, and nothing lined up. */}
          <div className="mt-4 grid items-start gap-x-4 gap-y-5 sm:grid-cols-3">
            <div>
              <label className="text-sm font-medium" htmlFor="timezone">
                {t("settings.timezone")}
              </label>
              <ConfigSelect
                id="timezone"
                options={TIMEZONE_OPTIONS}
                current={orgQuery.data?.timezone}
                {...register("timezone")}
              />
              {errors.timezone && (
                <p className="mt-1 text-xs text-destructive">{errors.timezone.message}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="currency">
                {t("settings.currency")}
              </label>
              <ConfigSelect
                id="currency"
                options={configOptions(t, CURRENCY_OPTIONS)}
                current={orgQuery.data?.currency}
                {...register("currency")}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="default_weight_unit">
                {t("settings.weightUnit")}
              </label>
              <ConfigSelect
                id="default_weight_unit"
                options={configOptions(t, WEIGHT_UNIT_OPTIONS)}
                current={orgQuery.data?.default_weight_unit}
                {...register("default_weight_unit")}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="country_code">
                {t("settings.country")}
              </label>
              {/* A picker for the same reason the language field is one: this is compared
                  exactly against `inventory_lists.country_code`, and free text on either side
                  of that comparison is what let `de` face `DE` and resolve to no country tier
                  (POS2-1144). "Not set" is offered only to an org that has none — like the
                  language below, `exclude_none` means a country once set cannot be cleared
                  here, so offering it would be a control that silently does nothing. */}
              <ConfigSelect
                id="country_code"
                options={
                  orgQuery.data?.country_code
                    ? configOptions(t, COUNTRY_OPTIONS)
                    : [
                        { value: "", label: t("settings.countryUnset") },
                        ...configOptions(t, COUNTRY_OPTIONS),
                      ]
                }
                current={orgQuery.data?.country_code}
                {...register("country_code")}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t("settings.countryHint")}</p>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="default_locale">
                {t("settings.language")}
              </label>
              {/* "Not set" is offered only to an organisation that has no default, so choosing
                  it is always a no-op — `PATCH /organisations/me` runs `exclude_none`, so a
                  language once set cannot be handed back to the browser from here. Offering an
                  option that silently does nothing would be worse than not offering it. */}
              <ConfigSelect
                id="default_locale"
                options={
                  orgQuery.data?.default_locale
                    ? LANGUAGE_OPTIONS
                    : [{ value: "", label: t("settings.languageUnset") }, ...LANGUAGE_OPTIONS]
                }
                current={orgQuery.data?.default_locale}
                {...register("default_locale")}
              />
              <p className="mt-1 text-xs text-muted-foreground">{t("settings.languageHint")}</p>
            </div>
          </div>
          <div className="mt-6 max-w-xs">
            <label className="text-sm font-medium" htmlFor="active_catalogue">
              {t("settings.activeCatalogue")}
            </label>
            <Input
              id="active_catalogue"
              value={activeCatalogueQuery.data ?? "—"}
              disabled
              readOnly
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("settings.activeCatalogueHint")}
            </p>
          </div>
          <div className="mt-4 max-w-xs">
            <label className="text-sm font-medium" htmlFor="food_cost_target_pct">
              {t("settings.foodCostTargetPct")}
            </label>
            <Input
              id="food_cost_target_pct"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder={t("settings.foodCostTargetPlaceholder")}
              {...register("food_cost_target_pct")}
            />
            {errors.food_cost_target_pct && (
              <p className="mt-1 text-xs text-destructive">{errors.food_cost_target_pct.message}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.foodCostTargetHint")}</p>
          </div>
          <div className="mt-4 max-w-xs">
            <label className="text-sm font-medium" htmlFor="waste_pct_target">
              {t("settings.wasteTargetPct")}
            </label>
            <Input
              id="waste_pct_target"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder={t("settings.wastePctTargetPlaceholder")}
              {...register("waste_pct_target")}
            />
            {errors.waste_pct_target && (
              <p className="mt-1 text-xs text-destructive">{errors.waste_pct_target.message}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.wasteTargetHint")}</p>
          </div>
          <div className="mt-4 max-w-xs">
            <label className="text-sm font-medium" htmlFor="waste_per_cover_target_g">
              {t("settings.wastePerCoverTargetG")}
            </label>
            <Input
              id="waste_per_cover_target_g"
              type="number"
              step="1"
              min="1"
              placeholder={t("settings.wastePerCoverTargetPlaceholder")}
              {...register("waste_per_cover_target_g")}
            />
            {errors.waste_per_cover_target_g && (
              <p className="mt-1 text-xs text-destructive">
                {errors.waste_per_cover_target_g.message}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.wastePerCoverHint")}</p>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-8 border-t border-border/50 pt-8">
          <h2 className="text-sm font-semibold">{t("settings.contact")}</h2>
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium" htmlFor="contact_email">
                  {t("settings.email")}
                </label>
                <Input id="contact_email" type="email" {...register("contact_email")} />
                {errors.contact_email && (
                  <p className="mt-1 text-xs text-destructive">{errors.contact_email.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="contact_phone">
                  {t("settings.phone")}
                </label>
                <Input id="contact_phone" type="tel" {...register("contact_phone")} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="address">
                {t("settings.address")}
              </label>
              <Input id="address" {...register("address")} />
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="mt-8 border-t border-border/50 pt-8">
          <h2 className="text-sm font-semibold">{t("settings.branding")}</h2>
          {/* The same component `/admin/organisations` has used since POS2-1132 (POS2-1149). What
              stood here was a bare text input beside a `div` whose colour read
              `orgQuery.data.primary_color` — the *server* value — so typing a new hex moved nothing
              until Save. */}
          <div className="mt-4 max-w-md">
            {/* `shouldDirty` matters here and not in the admin dialogs: this page gates Save on
                `isDirty`, so without it a colour-only change left Save disabled for good. */}
            <BrandColourField
              t={t}
              id="primary_color"
              logoKey={orgQuery.data?.logo_key ?? null}
              value={colour ?? ""}
              onChange={(hex) =>
                setValue("primary_color", hex, { shouldValidate: true, shouldDirty: true })
              }
              error={errors.primary_color?.message}
            />
          </div>
          {/* The same component the platform-admin dialog uses (POS2-1146), against the
              org-scoped routes — so the key is built from this caller's own organisation and
              there is no field through which another organisation's could be named. */}
          <div className="mt-4 max-w-md">
            <BrandLogoField
              t={t}
              logoPath="/organisations/me/logo"
              initialLogoKey={orgQuery.data?.logo_key ?? null}
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ["organisation", "me"] });
                queryClient.invalidateQueries({ queryKey: ["organisation", "brand"] });
              }}
            />
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button type="submit" disabled={!isDirty || !isValid || updateMutation.isPending}>
            {updateMutation.isPending ? t("common.saving") : t("settings.saveChangesButton")}
          </Button>
        </div>
      </form>
    </div>
  );
}
